import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CustomerLayout from '../CustomerLayout';
import { supabase, useAuth } from '../../context/AuthContext';
import { convertToMelbourneDate, convertToMelbourneTime } from '../../utils/dateUtils';

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

const ARCHIVED_BY_FILTERS = [
  { value: 'all', label: 'All archived' },
  { value: 'owner', label: 'Archived by owner' },
  { value: 'admin', label: 'Archived by admin' },
  { value: 'me', label: 'Archived by me' },
];

const archivedByLabel = (role) => {
  const normalized = String(role || '').trim().toLowerCase();
  if (normalized === 'owner') return 'Owner';
  if (normalized === 'admin') return 'Admin';
  if (normalized === 'customer') return 'Me';
  return '';
};

const carDetailsLabel = (row) => [row.car_make, row.car_model].filter(Boolean).join(' ');

const searchTokens = (value) =>
  String(value || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const matchesCarDetails = (row, query) => {
  const carTokens = searchTokens(`${row.car_make || ''} ${row.car_model || ''}`);
  const queryTokens = searchTokens(query);
  if (!carTokens.length || !queryTokens.length) return false;
  const carCompact = carTokens.join('');
  return queryTokens.every((token) =>
    carTokens.some((carToken) => carToken.includes(token)) || carCompact.includes(token)
  );
};

const PaginationControls = ({ currentPage, totalCount, onPageChange }) => {
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  if (totalPages <= 1) return null;

  const maxVisiblePages = 5;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  const pageNumbers = [];
  for (let page = startPage; page <= endPage; page += 1) {
    pageNumbers.push(page);
  }

  const indexOfLast = currentPage * PAGE_SIZE;
  const indexOfFirst = indexOfLast - PAGE_SIZE;

  return (
    <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <p className="text-sm text-slate-700">
          Showing <span className="font-medium">{indexOfFirst + 1}</span> to{' '}
          <span className="font-medium">{Math.min(indexOfLast, totalCount)}</span> of{' '}
          <span className="font-medium">{totalCount}</span> results
        </p>
        <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-l-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="sr-only">Previous</span>
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          {startPage > 1 && (
            <>
              <button
                type="button"
                onClick={() => onPageChange(1)}
                className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                1
              </button>
              {startPage > 2 && (
                <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                  ...
                </span>
              )}
            </>
          )}
          {pageNumbers.map((number) => (
            <button
              key={number}
              type="button"
              onClick={() => onPageChange(number)}
              className={`relative inline-flex items-center border px-4 py-2 text-sm font-medium ${
                currentPage === number
                  ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {number}
            </button>
          ))}
          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && (
                <span className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
                  ...
                </span>
              )}
              <button
                type="button"
                onClick={() => onPageChange(totalPages)}
                className="relative inline-flex items-center border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {totalPages}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center rounded-r-[0.75rem] border border-slate-300 bg-white px-2 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="sr-only">Next</span>
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
};

const ExpandableText = ({ text, maxLines = 2, className = 'w-64 max-w-[16rem]', textClassName = '' }) => {
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el || expanded) return;
    setCanExpand(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded, maxLines]);

  if (!text) {
    return <span className={textClassName || 'text-slate-400'}>-</span>;
  }

  return (
    <div className={className}>
      <p
        ref={textRef}
        className={`whitespace-pre-wrap break-words ${textClassName}`}
        style={expanded ? undefined : {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {text}
      </p>
      {(canExpand || expanded) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((open) => !open);
          }}
          className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus:underline"
        >
          {expanded ? 'View less' : 'View more'}
        </button>
      )}
    </div>
  );
};

const CustomerArchivedOrders = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [archivedByFilter, setArchivedByFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [isNarrowTable, setIsNarrowTable] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1280px)').matches;
  });
  const [isSmallTable, setIsSmallTable] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 640px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery1280 = window.matchMedia('(max-width: 1280px)');
    const mediaQuery640 = window.matchMedia('(max-width: 640px)');
    const handleViewportChange = () => {
      setIsNarrowTable(mediaQuery1280.matches);
      setIsSmallTable(mediaQuery640.matches);
    };

    handleViewportChange();

    if (typeof mediaQuery1280.addEventListener === 'function') {
      mediaQuery1280.addEventListener('change', handleViewportChange);
      mediaQuery640.addEventListener('change', handleViewportChange);
      return () => {
        mediaQuery1280.removeEventListener('change', handleViewportChange);
        mediaQuery640.removeEventListener('change', handleViewportChange);
      };
    }

    mediaQuery1280.addListener(handleViewportChange);
    mediaQuery640.addListener(handleViewportChange);
    return () => {
      mediaQuery1280.removeListener(handleViewportChange);
      mediaQuery640.removeListener(handleViewportChange);
    };
  }, []);

  useEffect(() => {
    setExpandedRowId(null);
  }, [currentPage, searchValue, archivedByFilter, sortOrder, isNarrowTable, isSmallTable]);

  const toggleExpandedRow = (id) => {
    setExpandedRowId((current) => (current === id ? null : id));
  };

  const fetchArchived = useCallback(async () => {
    if (!user?.id) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('admin_submissions_view')
        .select(`
          id,
          vin_number,
          car_make,
          car_model,
          mr_approval,
          archived_at,
          archived_by_role,
          archive_reason,
          created_at
        `)
        .eq('user_id', user.id)
        .eq('is_archived', true)
        .order('archived_at', { ascending: false });

      if (fetchError) throw fetchError;
      setRows(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load archived orders');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchArchived();
  }, [fetchArchived]);

  const visibleRows = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const filtered = rows.filter((row) => {
      if (archivedByFilter !== 'all' && archivedByLabel(row.archived_by_role).toLowerCase() !== archivedByFilter) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        row.mr_approval,
        carDetailsLabel(row),
        row.vin_number,
        row.archive_reason,
        archivedByLabel(row.archived_by_role),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query) || matchesCarDetails(row, query);
    });

    return [...filtered].sort((a, b) => {
      const aTime = a.archived_at ? new Date(a.archived_at).getTime() : 0;
      const bTime = b.archived_at ? new Date(b.archived_at).getTime() : 0;
      return sortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });
  }, [rows, searchValue, archivedByFilter, sortOrder]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [visibleRows.length, currentPage]);

  const pageRows = visibleRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const serialOffset = (currentPage - 1) * PAGE_SIZE;

  return (
    <CustomerLayout
      activeItemId="archived-orders"
      title="Archived Orders"
      subtitle="Read-only list of orders you archived. Contact admin if you need one restored."
      contentClassName="mx-auto w-full max-w-none"
    >
      <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,0.9fr)_minmax(0,0.7fr)_auto]">
          <div className="relative">
            <input
              type="text"
              value={searchValue}
              onChange={(e) => {
                setSearchValue(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search MRE, make or model, VIN, reason, or who archived..."
              className="w-full rounded-2xl border border-slate-300 bg-white px-11 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <svg
              className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <select
            value={archivedByFilter}
            onChange={(e) => {
              setArchivedByFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            aria-label="Filter by who archived"
          >
            {ARCHIVED_BY_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            aria-label="Sort by archived at"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={fetchArchived}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
            title="Reload orders"
          >
            <svg
              className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-6 py-4">
          <h2 className="text-md font-medium uppercase tracking-[0.04em] text-slate-900">
            My Archived Orders
          </h2>
          <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium uppercase tracking-[0.04em] text-slate-500">
            Total:
            <span className="text-sm font-semibold normal-case tracking-normal text-slate-900">{visibleRows.length}</span>
          </p>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading...</div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No archived orders.</div>
        ) : visibleRows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">No archived orders match your filters.</div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {isNarrowTable && <th className="w-12 px-3 py-3" aria-label="Details" />}
                  {!isSmallTable && (
                    <th className="w-16 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">S No.</th>
                  )}
                  {!isSmallTable && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Archived At</th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">MRE No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Car Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">VIN</th>
                  {!isNarrowTable && (
                    <th className="w-64 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Reason</th>
                  )}
                  {!isNarrowTable && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Archived By</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {pageRows.map((row, index) => {
                  const isExpanded = isNarrowTable && expandedRowId === row.id;
                  const archivedBy = archivedByLabel(row.archived_by_role);

                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={`transition-colors ${isExpanded ? 'bg-slate-50' : 'hover:bg-slate-50'} ${isNarrowTable ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (isNarrowTable) toggleExpandedRow(row.id);
                        }}
                      >
                        {isNarrowTable && (
                          <td className="px-3 py-3 whitespace-nowrap">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpandedRow(row.id);
                              }}
                              aria-expanded={isExpanded}
                              aria-label={isExpanded ? 'Collapse archive details' : 'Expand archive details'}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            >
                              <ChevronDownIcon
                                className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                                aria-hidden="true"
                              />
                            </button>
                          </td>
                        )}
                        {!isSmallTable && (
                          <td className="px-4 py-3 whitespace-nowrap text-slate-500">{serialOffset + index + 1}</td>
                        )}
                        {!isSmallTable && (
                          <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                            {row.archived_at ? (
                              <div className="leading-5">
                                <div className="text-black">{convertToMelbourneDate(row.archived_at)}</div>
                                <div className="text-xs text-slate-500">{convertToMelbourneTime(row.archived_at)}</div>
                              </div>
                            ) : '-'}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-slate-700">{row.mr_approval || '-'}</td>
                        <td className="px-4 py-3 text-slate-700">{carDetailsLabel(row) || '-'}</td>
                        <td className="px-4 py-3 break-all text-slate-700">{row.vin_number || '-'}</td>
                        {!isNarrowTable && (
                          <td className="w-64 px-4 py-3 text-slate-700">
                            <ExpandableText text={row.archive_reason} />
                          </td>
                        )}
                        {!isNarrowTable && (
                          <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                            {archivedBy || '-'}
                          </td>
                        )}
                      </tr>
                      {isExpanded && (
                        <tr className="bg-slate-50/70">
                          <td colSpan={isSmallTable ? 4 : 6} className="px-3 pb-4 pt-0 sm:px-4">
                            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                              <div className="flex items-center gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-4 sm:px-5">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                                  <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
                                  </svg>
                                </span>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-slate-900">Archive details</p>
                                  <p className="text-xs text-slate-500">
                                    {isSmallTable
                                      ? 'Archived date, who archived it, and the reason.'
                                      : 'Who archived it, and the reason.'}
                                  </p>
                                </div>
                              </div>
                              <div className={`grid gap-3 p-3 sm:p-4 ${
                                isSmallTable
                                  ? 'grid-cols-1 min-[480px]:grid-cols-2'
                                  : 'md:grid-cols-[13rem_minmax(0,1fr)]'
                              }`}>
                                {isSmallTable && (
                                  <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Archived At</p>
                                    {row.archived_at ? (
                                      <div className="mt-2 leading-5">
                                        <p className="break-words text-xs font-normal text-slate-700 xl:text-sm">{convertToMelbourneDate(row.archived_at)}</p>
                                        <p className="break-words text-xs font-normal text-slate-700 xl:text-sm">{convertToMelbourneTime(row.archived_at)}</p>
                                      </div>
                                    ) : (
                                      <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">-</p>
                                    )}
                                  </div>
                                )}
                                <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Archived By</p>
                                  <p className="mt-2 break-words text-xs font-normal text-slate-700 xl:text-sm">{archivedBy || '-'}</p>
                                </div>
                                <div className={`min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 ${isSmallTable ? 'min-[480px]:col-span-2' : ''}`}>
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Reason</p>
                                  <div className="mt-2">
                                    <ExpandableText
                                      text={row.archive_reason}
                                      className="w-full max-w-none"
                                      textClassName="text-xs font-normal text-slate-700 xl:text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalCount={visibleRows.length}
            onPageChange={setCurrentPage}
          />
          </>
        )}
      </div>
    </CustomerLayout>
  );
};

export default CustomerArchivedOrders;
